import type { JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { CalculatorResults } from "../engine";
import type { Household } from "./Form";
import { fallbackPrint, generatePdfReport } from "./pdf";

interface EmailGateProps {
  results: CalculatorResults;
  colaRate: number;
  household: Household;
  primaryLongevity: number;
  spouseLongevity: number;
  onClose: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Phase = "form" | "submitting" | "generating" | "done" | "error";

export function EmailGate({
  results,
  colaRate,
  household,
  onClose,
}: EmailGateProps): JSX.Element {
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [phase, setPhase] = useState<Phase>("form");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const input = dialogRef.current?.querySelector<HTMLInputElement>(
      "input[type=email]"
    );
    input?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  async function handleSubmit(e: JSX.TargetedEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrMsg(null);
    if (!EMAIL_RE.test(email)) {
      setErrMsg("Please enter a valid email address.");
      return;
    }
    setPhase("submitting");

    // Post to GHL webhook with NON-PII metadata only.
    // Never send DOB, PIA, or benefit dollar amounts.
    const cfg = window.tcaSsCalc;
    const webhookUrl = cfg?.webhookUrl;
    const payload = {
      email: email.trim(),
      newsletter_opt_in: optIn,
      tag: "ss-calculator-lead",
      source: "tca-ss-calculator",
      household, // "single" | "couple"
      cola_rate: colaRate,
      primary_custom_claim_age: results.primary.benefits.custom.claimAge,
      spouse_custom_claim_age:
        results.spouse?.benefits.custom.claimAge ?? null,
      engine_version: results.meta.engineVersion,
    };
    try {
      if (webhookUrl) {
        await fetch(webhookUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      // We don't gate the PDF on webhook success — the soft gate captures
      // the email, but the user still gets their PDF either way.
    } catch {
      // Swallow webhook errors silently to UX — the lead can be recaptured
      // on a future visit. Still log to console for debugging.
      // eslint-disable-next-line no-console
      console.warn("[tca-ss-calc] webhook post failed");
    }

    setPhase("generating");
    try {
      await generatePdfReport({ results, household, colaRate });
      setPhase("done");
    } catch (err) {
      // CDN blocked / offline / CSP — fall back to native print dialog.
      // eslint-disable-next-line no-console
      console.warn("[tca-ss-calc] PDF generation failed, falling back to print:", err);
      try {
        fallbackPrint();
        setPhase("done");
      } catch {
        setPhase("error");
        setErrMsg(
          "We couldn't generate the PDF. Try printing the page to PDF with your browser (Cmd/Ctrl-P)."
        );
      }
    }
  }

  return (
    <div
      class="tca-ss-calc__modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        class="tca-ss-calc__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tca-ss-email-title"
        ref={dialogRef}
      >
        <button
          type="button"
          class="tca-ss-calc__modal-close"
          aria-label="Close"
          onClick={onClose}
        >
          ×
        </button>
        <h3 id="tca-ss-email-title">Get your scenario as a PDF</h3>

        {phase === "done" ? (
          <div>
            <p>
              Your PDF is downloading. We'll send a copy of the report
              instructions to <strong>{email}</strong>.
            </p>
            <button
              type="button"
              class="tca-ss-calc__btn tca-ss-calc__btn--primary"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <p class="tca-ss-calc__modal-lede">
              Enter your email to download a PDF of this scenario. We don't
              share your email; we never collect your date of birth, PIA, or
              benefit amounts on our servers.
            </p>
            <label class="tca-ss-calc__label" for="tca-ss-email">
              Email address
            </label>
            <input
              id="tca-ss-email"
              class="tca-ss-calc__input"
              type="email"
              required
              autoComplete="email"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              disabled={phase !== "form"}
            />
            <label class="tca-ss-calc__checkbox">
              <input
                type="checkbox"
                checked={optIn}
                onChange={(e) =>
                  setOptIn((e.target as HTMLInputElement).checked)
                }
                disabled={phase !== "form"}
              />
              <span>Send me the weekly TCA newsletter (unsubscribe anytime)</span>
            </label>

            {errMsg && (
              <p class="tca-ss-calc__error" role="alert">
                {errMsg}
              </p>
            )}

            <div class="tca-ss-calc__actions">
              <button
                type="submit"
                class="tca-ss-calc__btn tca-ss-calc__btn--primary"
                disabled={phase === "submitting" || phase === "generating"}
              >
                {phase === "submitting"
                  ? "Sending…"
                  : phase === "generating"
                  ? "Preparing PDF…"
                  : "Download PDF"}
              </button>
              <button
                type="button"
                class="tca-ss-calc__btn tca-ss-calc__btn--ghost"
                onClick={onClose}
                disabled={phase === "submitting" || phase === "generating"}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
